// recova GmbH – 5-stufiger Buchungs-Wizard inkl. Typenschild-Guide und Foto-Upload.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, uploadPhoto, fileUrl, fmtErr, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Recycle, WashingMachine, Droplets, Wind, Flame, Snowflake, ArrowLeft, ArrowRight, CheckCircle2, Camera, Info, Copy } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICONS = { waschmaschine: WashingMachine, spuelmaschine: Droplets, trockner: Wind, backofen: Flame, kuehlschrank: Snowflake };
const STEP_TITLES = ["Gerät & Marke", "Typenschild", "Fehlerbeschreibung", "Kontakt & Termin", "Zusammenfassung"];

const empty = {
  appliance: { type: "", brand: "", model: "", e_number: "", type_photo: "" },
  fault: { error_code: "", issues: [], description: "", photo: "" },
  customer: { name: "", email: "", phone: "", street: "", zip: "", city: "" },
  scheduled_date: "",
  time_window: "",
};

export default function BookingPage() {
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    API.get("/meta/appliances").then((r) => setMeta(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  if (!meta) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1b4332] border-t-transparent" /></div>;

  const set = (section, key, val) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));
  const toggleIssue = (issue) =>
    setForm((f) => ({ ...f, fault: { ...f.fault, issues: f.fault.issues.includes(issue) ? f.fault.issues.filter((i) => i !== issue) : [...f.fault.issues, issue] } }));

  const canNext = () => {
    if (step === 0) return form.appliance.type && form.appliance.brand;
    if (step === 2) return form.fault.description || form.fault.issues.length > 0 || form.fault.error_code;
    if (step === 3) return form.customer.name && form.customer.email && form.customer.phone && form.customer.street && form.customer.zip && form.customer.city;
    return true;
  };

  const onPhoto = async (section, key, file) => {
    if (!file) return;
    try {
      const url = await uploadPhoto(file);
      set(section, key, url);
      toast.success("Foto hochgeladen");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await API.post("/public/booking", form);
      setResult(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const typeMeta = form.appliance.type ? meta.appliances[form.appliance.type] : null;

  if (result)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8faf9] px-4">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-xl" data-testid="booking-success">
          <CheckCircle2 className="mx-auto h-16 w-16 text-[#2a9d8f]" />
          <h2 className="font-heading mt-4 text-2xl font-bold text-[#1b4332]">Anfrage erfolgreich übermittelt!</h2>
          <p className="mt-2 text-sm text-slate-500">Ihre Auftragsnummer:</p>
          <p className="font-mono2 mt-1 text-2xl font-bold text-[#0f172a]" data-testid="booking-order-number">{result.order_number}</p>
          <p className="mt-4 text-sm text-slate-600">Sie erhalten in Kürze eine Bestätigung per E-Mail. Den Fortschritt können Sie jederzeit live verfolgen:</p>
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-100 p-3">
            <code className="flex-1 truncate text-left text-xs text-[#028090]" data-testid="booking-tracking-url">{result.tracking_url}</code>
            <Button size="sm" variant="outline" data-testid="copy-tracking-link" onClick={() => { navigator.clipboard.writeText(result.tracking_url); toast.success("Link kopiert"); }}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Link to={`/status/${result.tracking_token}`} data-testid="goto-tracking">
            <button className="btn-primary mt-6 w-full" data-testid="goto-tracking-button">Auftragsstatus jetzt ansehen</button>
          </Link>
          <Link to="/" className="mt-4 inline-block text-sm text-slate-500 hover:text-[#1b4332]">Zurück zur Startseite</Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#f8faf9]">
      <header className="glass-header">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="booking-logo">
            <img src="/logo.png" alt="recova Logo" className="h-9 w-9 rounded-full object-cover" data-testid="logo" />
            <span className="font-heading text-lg font-extrabold text-[#1b4332]">recova</span>
          </Link>
          <span className="badge-tech">Diagnosepauschale {eur(meta.diagnosis_fee)} – wird verrechnet</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <h1 className="font-heading text-2xl font-bold text-[#1b4332] sm:text-3xl" data-testid="booking-title">Reparatur-Termin anfragen</h1>
        <div className="mb-8 mt-5 flex items-center gap-1.5" data-testid="wizard-progress">
          {STEP_TITLES.map((t, i) => (
            <div key={i} className="flex flex-1 flex-col gap-1.5">
              <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-[#1b4332]" : "bg-slate-200"}`} />
              <span className={`hidden text-[11px] font-medium sm:block ${i === step ? "text-[#1b4332]" : "text-slate-400"}`}>{t}</span>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl md:p-10" data-testid={`wizard-step-${step + 1}`}>
          {step === 0 && (
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#0f172a]">Welches Gerät ist defekt?</h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {Object.entries(meta.appliances).map(([key, a]) => {
                  const Icon = TYPE_ICONS[key] || WashingMachine;
                  const sel = form.appliance.type === key;
                  return (
                    <button key={key} data-testid={`appliance-${key}`} onClick={() => set("appliance", "type", key)}
                      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-5 transition-all duration-200 active:scale-95 ${sel ? "border-[#1b4332] bg-[#e8f0ec]" : "border-slate-200 bg-white hover:border-[#2ec4b6]"}`}>
                      <Icon className={`h-8 w-8 ${sel ? "text-[#1b4332]" : "text-slate-400"}`} />
                      <span className={`text-sm font-semibold ${sel ? "text-[#1b4332]" : "text-slate-600"}`}>{a.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="brand">Marke *</Label>
                  <select id="brand" data-testid="brand-select" value={form.appliance.brand} onChange={(e) => set("appliance", "brand", e.target.value)}
                    className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#1b4332] focus:outline-none">
                    <option value="">Bitte wählen…</option>
                    {meta.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="model">Modell (optional)</Label>
                  <Input id="model" data-testid="model-input" className="mt-1.5" placeholder="z. B. WAN282ECO" value={form.appliance.model} onChange={(e) => set("appliance", "model", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#0f172a]">Typenschild-Guide – {typeMeta?.label}</h2>
              <div className="mt-4 rounded-xl border border-[#028090]/30 bg-cyan-50 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#028090]"><Info className="h-4 w-4" /> Hier finden Sie das Typenschild:</p>
                <ul className="mt-2 list-inside list-disc text-sm text-slate-600" data-testid="typenschild-positions">
                  {typeMeta?.positions.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="enumber">E-Nummer / PNC (optional)</Label>
                  <Input id="enumber" data-testid="enumber-input" className="mt-1.5 font-mono2" placeholder="z. B. WAN282ECO/01" value={form.appliance.e_number} onChange={(e) => set("appliance", "e_number", e.target.value)} />
                  <p className="mt-1 text-xs text-slate-500">Hilft uns, passende Ersatzteile vorab zu disponieren.</p>
                </div>
                <div>
                  <Label>Foto vom Typenschild (optional)</Label>
                  <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-4 text-sm text-slate-500 transition-colors hover:border-[#2ec4b6] hover:text-[#1b4332]" data-testid="typenschild-photo-upload">
                    <Camera className="h-5 w-5" /> {form.appliance.type_photo ? "Foto hochgeladen ✓" : "Foto aufnehmen / wählen"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto("appliance", "type_photo", e.target.files[0])} />
                  </label>
                  {form.appliance.type_photo && <img src={fileUrl(form.appliance.type_photo)} alt="Typenschild" className="mt-2 h-20 rounded-lg border object-cover" />}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#0f172a]">Was ist das Problem?</h2>
              <div className="mt-4">
                <Label htmlFor="errcode">Fehlercode (falls angezeigt)</Label>
                <Input id="errcode" data-testid="error-code-input" className="mt-1.5 w-40 font-mono2" placeholder="z. B. E18" value={form.fault.error_code} onChange={(e) => set("fault", "error_code", e.target.value)} />
              </div>
              <div className="mt-4">
                <Label>Häufige Probleme bei {typeMeta?.label}</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="issue-checklist">
                  {typeMeta?.issues.map((issue) => {
                    const sel = form.fault.issues.includes(issue);
                    return (
                      <button key={issue} type="button" data-testid={`issue-${issue.slice(0, 20).replace(/\W+/g, "-")}`} onClick={() => toggleIssue(issue)}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${sel ? "border-[#1b4332] bg-[#1b4332] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-[#2ec4b6]"}`}>
                        {issue}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4">
                <Label htmlFor="desc">Beschreibung</Label>
                <Textarea id="desc" data-testid="fault-description" className="mt-1.5" rows={3} placeholder="Wann tritt der Fehler auf? Seit wann? Geräusche?" value={form.fault.description} onChange={(e) => set("fault", "description", e.target.value)} />
              </div>
              <div className="mt-4">
                <Label>Foto vom Schaden (optional)</Label>
                <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-4 text-sm text-slate-500 transition-colors hover:border-[#2ec4b6] hover:text-[#1b4332]" data-testid="fault-photo-upload">
                  <Camera className="h-5 w-5" /> {form.fault.photo ? "Foto hochgeladen ✓" : "Foto aufnehmen / wählen"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto("fault", "photo", e.target.files[0])} />
                </label>
                {form.fault.photo && <img src={fileUrl(form.fault.photo)} alt="Schaden" className="mt-2 h-20 rounded-lg border object-cover" />}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#0f172a]">Ihre Kontaktdaten &amp; Wunschtermin</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><Label htmlFor="cname">Name *</Label><Input id="cname" data-testid="customer-name" className="mt-1.5" value={form.customer.name} onChange={(e) => set("customer", "name", e.target.value)} /></div>
                <div><Label htmlFor="cphone">Telefon *</Label><Input id="cphone" data-testid="customer-phone" className="mt-1.5" value={form.customer.phone} onChange={(e) => set("customer", "phone", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label htmlFor="cemail">E-Mail *</Label><Input id="cemail" type="email" data-testid="customer-email" className="mt-1.5" value={form.customer.email} onChange={(e) => set("customer", "email", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label htmlFor="cstreet">Straße &amp; Hausnummer *</Label><Input id="cstreet" data-testid="customer-street" className="mt-1.5" value={form.customer.street} onChange={(e) => set("customer", "street", e.target.value)} /></div>
                <div><Label htmlFor="czip">PLZ *</Label><Input id="czip" data-testid="customer-zip" className="mt-1.5" value={form.customer.zip} onChange={(e) => set("customer", "zip", e.target.value)} /></div>
                <div><Label htmlFor="ccity">Ort *</Label><Input id="ccity" data-testid="customer-city" className="mt-1.5" value={form.customer.city} onChange={(e) => set("customer", "city", e.target.value)} /></div>
                <div><Label htmlFor="cdate">Wunschdatum (optional)</Label><Input id="cdate" type="date" data-testid="customer-date" className="mt-1.5" value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} /></div>
                <div>
                  <Label htmlFor="cwindow">Zeitfenster</Label>
                  <select id="cwindow" data-testid="customer-window" className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" value={form.time_window} onChange={(e) => setForm((f) => ({ ...f, time_window: e.target.value }))}>
                    <option value="">Keine Präferenz</option>
                    {meta.time_windows.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="booking-summary">
              <h2 className="font-heading text-lg font-semibold text-[#0f172a]">Zusammenfassung</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Gerät</dt><dd className="font-semibold text-[#0f172a]">{form.appliance.brand} {typeMeta?.label} {form.appliance.model && `(${form.appliance.model})`}</dd></div>
                {form.appliance.e_number && <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">E-Nummer</dt><dd className="font-mono2">{form.appliance.e_number}</dd></div>}
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Fehler</dt><dd className="max-w-[60%] text-right font-semibold">{[form.fault.error_code, ...form.fault.issues, form.fault.description].filter(Boolean).join(" · ")}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Adresse</dt><dd className="text-right font-semibold">{form.customer.street}, {form.customer.zip} {form.customer.city}</dd></div>
                {form.scheduled_date && <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Wunschtermin</dt><dd className="font-semibold">{form.scheduled_date} {form.time_window}</dd></div>}
                <div className="flex justify-between pt-1"><dt className="text-slate-500">Anfahrts- &amp; Diagnosepauschale</dt><dd className="font-bold text-[#1b4332]">{eur(meta.diagnosis_fee)} <span className="text-xs font-normal text-slate-400">(wird bei Reparatur verrechnet)</span></dd></div>
              </dl>
              <p className="mt-4 rounded-lg bg-[#e8f0ec] p-3 text-xs text-[#1b4332]">Zahlung erst nach Erledigung – vor Ort per EC-/Kreditkarte (Tap-to-Pay), PayPal oder Überweisung.</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="wizard-back">
              <ArrowLeft className="mr-1 h-4 w-4" /> Zurück
            </Button>
            {step < 4 ? (
              <button className="btn-primary" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Bitte Pflichtfelder ausfüllen")} data-testid="wizard-next">
                Weiter <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button className="btn-primary" onClick={submit} disabled={busy} data-testid="wizard-submit">
                {busy ? "Wird gesendet…" : "Anfrage verbindlich senden"}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
